import { useState } from 'react';
import DatePicker from '../../common/DatePicker';
import { useQuery } from '@tanstack/react-query';
import {
  Wrench,
  Loader2,
  CalendarDays,
  Activity,
  CheckCircle,
  XCircle,
  PackageCheck,
  PackageX,
} from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { CurrencyIcon } from '@/components/CurrencyIcon';
import { getCurrencySymbol } from '@/utils/currency';

interface BranchOverviewData {
  filter: string;
  dateRange: { start: string; end: string };
  summary: {
    totalServices: number;
    inProgress: number;
    ready: number;
    notReady: number;
    delivered: number;
    notDelivered: number;
    totalCollected: number;
    inProgressValue: number;
  };
  branches: Array<{
    id: string;
    name: string;
    code: string;
    totalServices: number;
    inProgress: number;
    ready: number;
    notReady: number;
    delivered: number;
    notDelivered: number;
    totalCollected: number;
    inProgressValue: number;
  }>;
}

const FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'last7days', label: 'Last 7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
] as const;

export default function BranchServiceOverview() {
  const [filter, setFilter] = useState<string>('today');
  const [customRange, setCustomRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  const { data, isLoading } = useQuery<BranchOverviewData>({
    queryKey: ['branchServiceOverview', filter, customRange],
    queryFn: () =>
      dashboardApi.getBranchServiceOverview({
        filter,
        ...(filter === 'custom' ? customRange : {}),
      }),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  const summary = data?.summary;
  const branches = data?.branches || [];

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Branch Service Overview</h2>
            <p className="text-sm text-gray-500 mt-0.5">Service status breakdown by branch</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date range inputs */}
        {filter === 'custom' && (
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">From:</span>
              <DatePicker
                value={customRange.startDate}
                onChange={(v) => setCustomRange((prev) => ({ ...prev, startDate: v }))}
                wrapperClassName="w-40"
                className="!py-1.5"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">To:</span>
              <DatePicker
                value={customRange.endDate}
                onChange={(v) => setCustomRange((prev) => ({ ...prev, endDate: v }))}
                wrapperClassName="w-40"
                className="!py-1.5"
              />
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      )}

      {/* Content */}
      {!isLoading && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 p-4 sm:p-6">
            <SummaryCard
              icon={Wrench}
              label="Total Services"
              value={summary?.totalServices ?? 0}
              bgColor="bg-blue-50"
              iconColor="text-blue-600"
              textColor="text-blue-900"
            />
            <SummaryCard
              icon={Activity}
              label="In Progress"
              value={`${summary?.inProgress ?? 0} (${getCurrencySymbol()}${(summary?.inProgressValue ?? 0).toLocaleString()})`}
              bgColor="bg-amber-50"
              iconColor="text-amber-600"
              textColor="text-amber-900"
            />
            <SummaryCard
              icon={CheckCircle}
              label="Ready"
              value={summary?.ready ?? 0}
              bgColor="bg-green-50"
              iconColor="text-green-600"
              textColor="text-green-900"
            />
            <SummaryCard
              icon={XCircle}
              label="Not Ready"
              value={summary?.notReady ?? 0}
              bgColor="bg-red-50"
              iconColor="text-red-600"
              textColor="text-red-900"
            />
            <SummaryCard
              icon={PackageCheck}
              label="Delivered"
              value={summary?.delivered ?? 0}
              bgColor="bg-emerald-50"
              iconColor="text-emerald-600"
              textColor="text-emerald-900"
            />
            <SummaryCard
              icon={CurrencyIcon}
              label="Collected"
              value={`${getCurrencySymbol()}${(summary?.totalCollected ?? 0).toLocaleString()}`}
              bgColor="bg-cyan-50"
              iconColor="text-cyan-600"
              textColor="text-cyan-900"
            />
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    Branch
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">
                    Total
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">
                    In Progress
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">
                    Ready
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">
                    Not Ready
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">
                    Delivered
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">
                    Not Delivered
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    Collected
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {branches.map((branch) => (
                  <tr key={branch.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-purple-600 font-bold text-xs">{branch.code}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{branch.name}</span>
                      </div>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="text-sm font-semibold text-gray-900">{branch.totalServices}</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={branch.inProgress} color="amber" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={branch.ready} color="green" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={branch.notReady} color="red" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={branch.delivered} color="emerald" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={branch.notDelivered} color="orange" />
                    </td>
                    <td className="text-right px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {getCurrencySymbol()}{branch.totalCollected.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}

                {branches.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-500 text-sm">
                      No data available for the selected period
                    </td>
                  </tr>
                )}
              </tbody>

              {/* Totals Footer */}
              {branches.length > 0 && summary && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold border-t border-gray-200">
                    <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                    <td className="text-center px-3 py-3 text-sm text-gray-900">{summary.totalServices}</td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={summary.inProgress} color="amber" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={summary.ready} color="green" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={summary.notReady} color="red" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={summary.delivered} color="emerald" />
                    </td>
                    <td className="text-center px-3 py-3">
                      <StatusBadge count={summary.notDelivered} color="orange" />
                    </td>
                    <td className="text-right px-4 py-3 text-sm text-gray-900">
                      {getCurrencySymbol()}{summary.totalCollected.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-100">
            {branches.map((branch) => (
              <div key={branch.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-xs">{branch.code}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{branch.name}</span>
                  <span className="ml-auto text-sm font-bold text-gray-900">
                    {branch.totalServices} total
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <MobileStatItem label="In Progress" count={branch.inProgress} color="amber" />
                  <MobileStatItem label="Ready" count={branch.ready} color="green" />
                  <MobileStatItem label="Not Ready" count={branch.notReady} color="red" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <MobileStatItem label="Delivered" count={branch.delivered} color="emerald" />
                  <MobileStatItem label="Not Delvd" count={branch.notDelivered} color="orange" />
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Collected</p>
                    <p className="text-sm font-bold text-cyan-700">
                      {getCurrencySymbol()}{branch.totalCollected.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {branches.length === 0 && (
              <div className="text-center py-12 text-gray-500 text-sm">
                No data available for the selected period
              </div>
            )}

            {/* Mobile Totals */}
            {branches.length > 0 && summary && (
              <div className="p-4 bg-gray-50">
                <p className="text-sm font-bold text-gray-900 mb-3">
                  Total ({summary.totalServices} services)
                </p>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <MobileStatItem label="In Progress" count={summary.inProgress} color="amber" />
                  <MobileStatItem label="Ready" count={summary.ready} color="green" />
                  <MobileStatItem label="Not Ready" count={summary.notReady} color="red" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <MobileStatItem label="Delivered" count={summary.delivered} color="emerald" />
                  <MobileStatItem label="Not Delvd" count={summary.notDelivered} color="orange" />
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Collected</p>
                    <p className="text-sm font-bold text-cyan-700">
                      {getCurrencySymbol()}{summary.totalCollected.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  bgColor,
  iconColor,
  textColor,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  bgColor: string;
  iconColor: string;
  textColor: string;
}) {
  return (
    <div className={`${bgColor} rounded-xl p-3 sm:p-4`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${iconColor}`} />
        <span className={`text-[10px] sm:text-xs font-medium ${iconColor} uppercase leading-tight`}>
          {label}
        </span>
      </div>
      <p className={`text-xl sm:text-2xl font-bold ${textColor}`}>{value}</p>
    </div>
  );
}

function StatusBadge({
  count,
  color,
}: {
  count: number;
  color: 'amber' | 'blue' | 'green' | 'red' | 'emerald' | 'orange';
}) {
  if (count === 0) {
    return <span className="text-sm text-gray-400">0</span>;
  }

  const colorClasses: Record<string, string> = {
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    orange: 'bg-orange-100 text-orange-700',
  };

  return (
    <span
      className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 text-xs font-bold rounded-full ${colorClasses[color]}`}
    >
      {count}
    </span>
  );
}

function MobileStatItem({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: 'amber' | 'green' | 'red' | 'emerald' | 'orange';
}) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <StatusBadge count={count} color={color} />
    </div>
  );
}
